import { HttpClientProps, HttpClientResponse } from '../interfaces';
import { Method } from '../types';
import { prepareBody, prepareData, prepareHeaders, prepareUrl } from '../utils';

export class HttpClient {
  defaultHeaders: HeadersInit;
  private tokenRefresher?: () => Promise<string>;

  constructor(
    defaultHeaders?: HeadersInit,
    tokenRefresher?: () => Promise<string>
  ) {
    this.defaultHeaders = defaultHeaders ?? {};
    this.tokenRefresher = tokenRefresher;
  }

  public async get<TResponse = undefined, TParams = undefined>(
    data: HttpClientProps<never, TParams>
  ) {
    return await this.makeRequest<TResponse, never, TParams>('GET', data);
  }

  public async post<
    TResponse = undefined,
    TBody = undefined,
    TParams = undefined
  >(data: HttpClientProps<TBody, TParams>) {
    return await this.makeRequest<TResponse, TBody, TParams>('POST', data);
  }

  public async put<
    TResponse = undefined,
    TBody = undefined,
    TParams = undefined
  >(data: HttpClientProps<TBody, TParams>) {
    return await this.makeRequest<TResponse, TBody, TParams>('PUT', data);
  }

  public async delete<
    TResponse = undefined,
    TBody = undefined,
    TParams = undefined
  >(data: HttpClientProps<TBody, TParams>) {
    return await this.makeRequest<TResponse, TBody, TParams>('DELETE', data);
  }

  private async makeRequest<
    TResponse = undefined,
    TBody = undefined,
    TParams = undefined
  >(
    method: Method,
    data: HttpClientProps<TBody, TParams>
  ): Promise<HttpClientResponse<TResponse>> {
    const {
      url,
      params,
      headers,
      responseType = 'JSON',
      body,
      contentType,
    } = data;

    const preparedURL = prepareUrl<TParams>({ url, params });
    const preparedBody = prepareBody<TBody>({ body });

    try {
      const preparedHeaders = prepareHeaders({
        defaultHeaders: { ...this.defaultHeaders, ...headers },
        contentType,
      });

      const response = await fetch(preparedURL, {
        method: method,
        body: preparedBody,
        headers: preparedHeaders,
      });
      const editedResponse = {
        status: response.status,
        headers: response.headers,
        data: await prepareData<TResponse>({ response, responseType }),
      };

      // If request failed with 401 (Unauthorized) and we have a token refresher, try to refresh and retry
      if (!response.ok && response.status === 401 && this.tokenRefresher) {
        try {
          const newToken = await this.tokenRefresher();

          // Update the authorization header with the new token
          const updatedHeaders = {
            ...this.defaultHeaders,
            ...headers,
            Authorization: `bearer ${newToken}`,
          };

          const retryPreparedHeaders = prepareHeaders({
            defaultHeaders: updatedHeaders,
            contentType,
          });

          // Retry the request with the new token
          const retryResponse = await fetch(preparedURL, {
            method: method,
            body: preparedBody,
            headers: retryPreparedHeaders,
          });

          const retryEditedResponse = {
            status: retryResponse.status,
            headers: retryResponse.headers,
            data: await prepareData<TResponse>({
              response: retryResponse,
              responseType,
            }),
          };

          return retryResponse.ok
            ? retryEditedResponse
            : Promise.reject(retryEditedResponse);
        } catch (refreshError) {
          // If token refresh fails, return the original error
          // Token refresh failed, will return original error
          return Promise.reject(editedResponse);
        }
      }

      return response.ok ? editedResponse : Promise.reject(editedResponse);
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

export const httpClient = new HttpClient();
